import { useState } from 'react';
import { Shield, CheckCircle, XCircle, Sparkles } from 'lucide-react';

// Static, role-color → literal Tailwind class map. Dynamic `from-${roleColor}-500`
// interpolation does NOT get generated by Tailwind (no safelist), so the gradient/
// fade would silently break for some roles. These literal strings guarantee the
// "I understand" button + header render the same fade for EVERY role color.
const ROLE_STYLES = {
  amber:   { icon: 'from-amber-400 to-amber-600',     title: 'text-amber-400',   btn: 'from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 shadow-amber-500/20',         pill: 'bg-amber-500/10 border-amber-500/20',     pillText: 'text-amber-300',   box: 'bg-amber-500/5 border-amber-500/15' },
  violet:  { icon: 'from-violet-400 to-violet-600',   title: 'text-violet-400',  btn: 'from-violet-500 to-violet-600 hover:from-violet-400 hover:to-violet-500 shadow-violet-500/20',     pill: 'bg-violet-500/10 border-violet-500/20',   pillText: 'text-violet-300',  box: 'bg-violet-500/5 border-violet-500/15' },
  cyan:    { icon: 'from-cyan-400 to-cyan-600',       title: 'text-cyan-400',    btn: 'from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 shadow-cyan-500/20',                 pill: 'bg-cyan-500/10 border-cyan-500/20',       pillText: 'text-cyan-300',    box: 'bg-cyan-500/5 border-cyan-500/15' },
  emerald: { icon: 'from-emerald-400 to-emerald-600', title: 'text-emerald-400', btn: 'from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 shadow-emerald-500/20', pill: 'bg-emerald-500/10 border-emerald-500/20', pillText: 'text-emerald-300', box: 'bg-emerald-500/5 border-emerald-500/15' },
  teal:    { icon: 'from-teal-400 to-teal-600',       title: 'text-teal-400',    btn: 'from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 shadow-teal-500/20',                 pill: 'bg-teal-500/10 border-teal-500/20',       pillText: 'text-teal-300',    box: 'bg-teal-500/5 border-teal-500/15' },
  orange:  { icon: 'from-orange-400 to-orange-600',   title: 'text-orange-400',  btn: 'from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 shadow-orange-500/20',     pill: 'bg-orange-500/10 border-orange-500/20',   pillText: 'text-orange-300',  box: 'bg-orange-500/5 border-orange-500/15' },
  indigo:  { icon: 'from-indigo-400 to-indigo-600',   title: 'text-indigo-400',  btn: 'from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 shadow-indigo-500/20',     pill: 'bg-indigo-500/10 border-indigo-500/20',   pillText: 'text-indigo-300',  box: 'bg-indigo-500/5 border-indigo-500/15' },
  blue:    { icon: 'from-blue-400 to-blue-600',       title: 'text-blue-400',    btn: 'from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 shadow-blue-500/20',                 pill: 'bg-blue-500/10 border-blue-500/20',       pillText: 'text-blue-300',    box: 'bg-blue-500/5 border-blue-500/15' },
  sky:     { icon: 'from-sky-400 to-sky-600',         title: 'text-sky-400',     btn: 'from-sky-500 to-sky-600 hover:from-sky-400 hover:to-sky-500 shadow-sky-500/20',                     pill: 'bg-sky-500/10 border-sky-500/20',         pillText: 'text-sky-300',     box: 'bg-sky-500/5 border-sky-500/15' },
  slate:   { icon: 'from-slate-400 to-slate-600',     title: 'text-slate-400',   btn: 'from-slate-500 to-slate-600 hover:from-slate-400 hover:to-slate-500 shadow-slate-500/20',         pill: 'bg-slate-500/10 border-slate-500/20',     pillText: 'text-slate-300',   box: 'bg-slate-500/5 border-slate-500/15' },
};

export default function RoleOrientationCard({ orientationCard, roleColor, onStart }) {
  const [starting, setStarting] = useState(false);

  if (!orientationCard) return null;

  const styles = ROLE_STYLES[roleColor] || ROLE_STYLES.slate;

  const { loggedInAs, yourJob, mainWorkspace, workspaces, workspacesHeading, can, cannot, mainSuccessAction, noChaosRule } = orientationCard;
  const workspaceList = workspaces || [mainWorkspace];
  const workspaceHeading = workspacesHeading || 'Your Workspace';

  const handleStart = async () => {
    setStarting(true);
    try {
      await onStart();
    } catch {
      setStarting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div className="rg-surface-warm relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className={`px-6 pt-6 pb-4 border-b border-slate-800/50`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${styles.icon} flex items-center justify-center`}>
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-400">You are logged in as</p>
              <h2 className={`text-lg font-bold ${styles.title}`}>{loggedInAs}</h2>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Your Job</h3>
            <p className="text-white text-sm leading-relaxed">{yourJob}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-1.5">{workspaceHeading}</h3>
            <div className="flex flex-wrap gap-2">
              {workspaceList.map((ws, i) => (
                <div key={i} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${styles.pill}`}>
                  <Sparkles className={`w-4 h-4 ${styles.title}`} />
                  <span className={`text-sm font-medium ${styles.pillText}`}>{ws.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" /> You Can
              </h3>
              <ul className="space-y-1.5">
                {can.map((item, i) => (
                  <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5 flex-shrink-0">+</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <XCircle className="w-3.5 h-3.5" /> You Cannot
              </h3>
              <ul className="space-y-1.5">
                {cannot.map((item, i) => (
                  <li key={i} className="text-sm text-slate-300 flex items-start gap-2">
                    <span className="text-red-500 mt-0.5 flex-shrink-0">-</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className={`rounded-lg border px-4 py-3 ${styles.box}`}>
            <h3 className={`text-xs font-semibold ${styles.title} uppercase tracking-wider mb-1`}>Success Action</h3>
            <p className="text-sm text-white">{mainSuccessAction}</p>
          </div>

          <div className="rounded-lg bg-amber-500/5 border border-amber-500/15 px-4 py-3">
            <h3 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1">No-Chaos Rule</h3>
            <p className="text-sm text-slate-300">{noChaosRule}</p>
          </div>
        </div>

        <div className="px-6 pb-6 pt-2">
          <button
            onClick={handleStart}
            disabled={starting}
            className={`
              w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm
              bg-gradient-to-r ${styles.btn} text-white rg-cta-gradient
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200 shadow-lg
            `}
          >
            {starting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Starting...
              </>
            ) : (
              'I understand'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
